import React, { ReactNode } from "react";
import { cn } from "@/utils/styles";

interface ButtonProps {
  children: ReactNode; // Button text or content
  size?: "sm" | "md"; // Button size
  variant?: "primary" | "outline"; // Button variant
  startIcon?: ReactNode; // Icon before the text
  endIcon?: ReactNode; // Icon after the text
  onClick?: () => void; // Click handler
  disabled?: boolean; // Disabled state
  className?: string; // Custom className
  type?: "button" | "submit" | "reset"; // Button type
}

const Button: React.FC<ButtonProps> = ({
  children,
  size = "md",
  variant = "primary",
  startIcon,
  endIcon,
  onClick,
  className = "",
  disabled = false,
  type = "button",
}) => {
  // Size Classes
  const sizeClasses = {
    sm: "px-4 py-3 text-sm",
    md: "px-5 py-3.5 text-sm",
  };

  // Variant Classes
  const variantClasses = {
    primary:
      "bg-[color:var(--color-brand-primary)] text-white shadow-theme-xs hover:bg-[color:var(--color-brand-primary)]/90 disabled:bg-[color:var(--color-brand-primary)]/50",
    outline:
      "bg-white text-[color:var(--color-brand-primary)] ring-1 ring-inset ring-[color:var(--color-brand-primary)] hover:bg-[color:var(--color-brand-primary)]/10 dark:bg-gray-800 dark:text-[color:var(--color-brand-primary)] dark:ring-[color:var(--color-brand-primary)] dark:hover:bg-[color:var(--color-brand-primary)]/10",
  };

  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center font-medium gap-2 rounded-lg transition',
        sizeClasses[size],
        variantClasses[variant],
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
      onClick={onClick}
      disabled={disabled}
    >
      {startIcon && <span className="flex items-center">{startIcon}</span>}
      {children}
      {endIcon && <span className="flex items-center">{endIcon}</span>}
    </button>
  );
};

export default Button;
